import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Box, 
  Paper, 
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  CircularProgress,
  FormHelperText
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useNavigate, useParams } from 'react-router-dom';
import Alert from '@material-ui/lab/Alert';
import { firestore } from '../../firebase/config';
import { doc, getDoc, updateDoc } from 'firebase/firestore';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
  },
  paper: {
    width: '100%',
    padding: theme.spacing(3),
  },
  form: {
    width: '100%',
    marginTop: theme.spacing(2),
  },
  formControl: {
    width: '100%',
    marginBottom: theme.spacing(2),
  },
  buttons: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(3),
  },
  button: {
    marginLeft: theme.spacing(1),
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(3),
  },
}));

const CompanyEditPage = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postalCode: '',
    country: 'France',
    website: '',
    description: '',
    status: 'Active',
  });
  const [errors, setErrors] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    fetchCompany();
  }, [id]);

  const fetchCompany = async () => {
    try {
      // Simulation de récupération depuis Firestore
      // Dans une implémentation réelle, cela serait remplacé par un appel à l'API
      const companyDoc = doc(firestore, 'companies', id);
      const companySnapshot = await getDoc(companyDoc);
      
      if (companySnapshot.exists()) {
        setFormData(companySnapshot.data());
      } else {
        // Si l'entreprise n'existe pas, utiliser des données de démonstration
        const demoCompany = {
          name: 'Voyage Express',
          email: 'contact@voyage-express.com',
          phone: '+33123456789',
          address: '123 Rue de Paris',
          city: 'Paris',
          postalCode: '75001',
          country: 'France',
          website: 'www.voyage-express.com',
          description: 'Entreprise de transport de voyageurs',
          status: 'Active'
        };
        setFormData(demoCompany);
      }
    } catch (error) {
      console.error('Error fetching company:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors de la récupération des données de l\'entreprise',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Clear error when field is edited
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Le nom est requis';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'L\'email est requis';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Format d\'email invalide';
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = 'Le téléphone est requis';
    }
    
    if (!formData.address.trim()) {
      newErrors.address = 'L\'adresse est requise';
    }
    
    if (!formData.status) {
      newErrors.status = 'Le statut est requis';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    try {
      // Simulation de mise à jour dans Firestore
      // Dans une implémentation réelle, cela serait remplacé par un appel à l'API
      const companyDoc = doc(firestore, 'companies', id);
      await updateDoc(companyDoc, {
        ...formData,
        updatedAt: new Date()
      });
      
      setSnackbar({
        open: true,
        message: 'Entreprise mise à jour avec succès',
        severity: 'success'
      });
      
      // Rediriger vers la liste après un court délai
      setTimeout(() => {
        navigate('/admin/companies');
      }, 1500);
      
    } catch (error) {
      console.error('Error updating company:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors de la mise à jour de l\'entreprise',
        severity: 'error'
      });
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin/companies');
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box className={classes.root}>
      <Typography variant="h4" component="h1" gutterBottom>
        Modifier l'entreprise
      </Typography>
      
      <Paper className={classes.paper}>
        {loading ? (
          <div className={classes.loading}>
            <CircularProgress />
          </div>
        ) : (
          <form className={classes.form} onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  name="name"
                  label="Nom de l'entreprise"
                  variant="outlined"
                  fullWidth
                  required
                  value={formData.name}
                  onChange={handleChange}
                  error={!!errors.name}
                  helperText={errors.name}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  name="email"
                  label="Email"
                  variant="outlined"
                  fullWidth
                  required
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  error={!!errors.email}
                  helperText={errors.email}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  name="phone"
                  label="Téléphone"
                  variant="outlined"
                  fullWidth
                  required
                  value={formData.phone}
                  onChange={handleChange}
                  error={!!errors.phone}
                  helperText={errors.phone}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  name="website"
                  label="Site web"
                  variant="outlined"
                  fullWidth
                  value={formData.website}
                  onChange={handleChange}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  name="address"
                  label="Adresse"
                  variant="outlined"
                  fullWidth
                  required
                  value={formData.address}
                  onChange={handleChange}
                  error={!!errors.address}
                  helperText={errors.address}
                />
              </Grid>
              
              <Grid item xs={12} md={4}>
                <TextField
                  name="city"
                  label="Ville"
                  variant="outlined"
                  fullWidth
                  value={formData.city}
                  onChange={handleChange}
                />
              </Grid>
              
              <Grid item xs={12} md={4}>
                <TextField
                  name="postalCode"
                  label="Code postal"
                  variant="outlined"
                  fullWidth
                  value={formData.postalCode}
                  onChange={handleChange}
                />
              </Grid>
              
              <Grid item xs={12} md={4}>
                <TextField
                  name="country"
                  label="Pays"
                  variant="outlined"
                  fullWidth
                  value={formData.country}
                  onChange={handleChange}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  name="description"
                  label="Description"
                  variant="outlined"
                  fullWidth
                  multiline
                  rows={4}
                  value={formData.description}
                  onChange={handleChange}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl variant="outlined" className={classes.formControl} error={!!errors.status}>
                  <InputLabel id="status-label">Statut</InputLabel>
                  <Select
                    labelId="status-label"
                    id="status"
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    label="Statut"
                    required
                  >
                    <MenuItem value="Active">Actif</MenuItem>
                    <MenuItem value="Inactive">Inactif</MenuItem>
                    <MenuItem value="Pending">En attente</MenuItem>
                  </Select>
                  {errors.status && <FormHelperText>{errors.status}</FormHelperText>}
                </FormControl>
              </Grid>
            </Grid>
            
            <div className={classes.buttons}>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleCancel}
                className={classes.button}
              >
                Annuler
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                className={classes.button}
              >
                Enregistrer
              </Button>
            </div>
          </form>
        )}
      </Paper>
      
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CompanyEditPage;
